import React, { useState, useEffect, useRef } from 'react';
import { func } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import MapSettings from './Maps/MapSettings';
import {
  updateMapSettings,
  updateSelectedGeographicsTab,
  updateCompleteMapSettings,
} from '../../actions/geoMappingActions';
import {
  GEO_HEAT_MAP_MESSAGE,
  MAP_COLOR_CLASSES,
  SELECT_GEOGRAPHICS_TAB_OPTIONS,
  TABLE_MAP_VIEW,
} from './GeoMapConstant';
import {
  generateMapLegends,
  getClassIndexFromIndexGroup,
  populateIndexGroups,
  resetDataOnTargetRemove,
  getDropdownListForTableData,
} from '../../util/heatMapUtil';
import USHeatMapChart from './HeatMapSvg';
import GeoMapLegends from './GeoMapLegends';
import { toastError } from '../../util/toastUtils';
import initialState from '../../reducers/initialState';
import { exportFileTitle } from '../../util/CommonUtils';
import CustomSpinner from '../common/CustomSpinner';

const GeoUSHeatMap = ({ showHeatMap, handleExportGeoMap }) => {
  const dispatch = useDispatch();
  const {
    mapSettings,
    geographicsTableData,
    selectedGeographicsTabIndex,
    TargetDataGridData,
    isGeoMapTableDataLoading,
  } = useSelector(state => state.geomap);
  const mapSetting =
    mapSettings[
      SELECT_GEOGRAPHICS_TAB_OPTIONS[selectedGeographicsTabIndex].value
    ];

  useEffect(() => {
    const _mapSettings = resetDataOnTargetRemove(mapSettings);
    dispatch(updateCompleteMapSettings(_mapSettings));
  }, [JSON.stringify(TargetDataGridData)]);

  // const dropdownListForMap = SELECT_GEOGRAPHICS_TAB_OPTIONS.map((item) => item.label);
  // Temporary disable [county, zipcode] maps
  const dropdownListForMap = getDropdownListForTableData(
    geographicsTableData,
    SELECT_GEOGRAPHICS_TAB_OPTIONS
  );

  let currentMap;

  if (
    dropdownListForMap.length &&
    dropdownListForMap.includes(
      SELECT_GEOGRAPHICS_TAB_OPTIONS[selectedGeographicsTabIndex].label
    )
  ) {
    currentMap = selectedGeographicsTabIndex;
  } else {
    currentMap = SELECT_GEOGRAPHICS_TAB_OPTIONS[0].index;
    dispatch(updateSelectedGeographicsTab(currentMap));
  }

  const [view, setView] = useState(
    SELECT_GEOGRAPHICS_TAB_OPTIONS[currentMap].label
  );
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  const currentGeographicsTableData =
    geographicsTableData[SELECT_GEOGRAPHICS_TAB_OPTIONS[currentMap].value];

  const indexGroupCount = mapSetting.indexGroupCount;

  const getIndexRange = currentGeographicsTableData.map(item => item.i);

  const minIndex = getIndexRange.length
    ? mapSetting.minIndex || String(Math.min(...getIndexRange))
    : '0';
  const maxIndex = getIndexRange.length
    ? mapSetting.maxIndex || String(Math.max(...getIndexRange))
    : '0';

  const excludeMin = mapSetting.excludeMinIndex
    ? mapSetting.excludeMinIndex
    : '';
  const excludeMax = mapSetting.excludeMaxIndex
    ? mapSetting.excludeMaxIndex
    : '';

  const indexGroupRange = populateIndexGroups(
    minIndex,
    maxIndex,
    indexGroupCount
  ).reverse();

  useEffect(() => {
    if (!currentGeographicsTableData.length) {
      toastError(GEO_HEAT_MAP_MESSAGE.noTableData.replace('-1-', view));
    }
  }, [view]);

  useEffect(() => {
    return () => {
      dispatch(updateCompleteMapSettings(initialState.geomap.mapSettings));
      setIsIframeLoaded(false);
      iframeRef.current = null;
    };
  }, []);

  const handleViewChange = view => {
    const currentSelectedTab = SELECT_GEOGRAPHICS_TAB_OPTIONS.find(
      item => item.label === view
    ).index;
    setView(view);
    dispatch(updateSelectedGeographicsTab(currentSelectedTab));
  };

  const addClassesToMap = () => {
    currentGeographicsTableData.forEach(item => {
      const elem =
        isIframeLoaded && view === 'Zip Code'
          ? iframeRef.current.contentWindow.document.getElementById(item.id)
          : document.getElementById(item.id);

      if (elem) {
        const getColorIndex = getClassIndexFromIndexGroup(
          item.i,
          indexGroupRange,
          mapSetting.excludeMinIndex,
          mapSetting.excludeMaxIndex
        );
        elem.setAttribute(
          'class',
          getColorIndex === -1
            ? 'white'
            : getColorIndex === -2
            ? 'grey'
            : MAP_COLOR_CLASSES[indexGroupCount][getColorIndex]
        );
      }
    });
  };

  useEffect(() => {
    addClassesToMap();
  }, [
    currentGeographicsTableData,
    indexGroupCount,
    minIndex,
    maxIndex,
    currentMap,
    mapSetting.excludeMinIndex,
    mapSetting.excludeMaxIndex,
    isIframeLoaded,
  ]);

  const mapLegends = generateMapLegends(
    indexGroupRange,
    indexGroupCount,
    MAP_COLOR_CLASSES
  );

  const handleAction = action => {
    switch (action) {
      case 'export': {
        handleExportGeoMap(exportFileTitle('GeoMapping'), 'pptx');
        break;
      }
      case 'edit': {
        showHeatMap(TABLE_MAP_VIEW);
        break;
      }
      default:
    }
  };

  return (
    <div className="geomap-us-settings">
      <MapSettings
        view={view}
        data={dropdownListForMap}
        handleViewChange={handleViewChange}
        mapSettings={mapSetting}
        runReport={() => ''}
        geoLabel={mapSetting.geoLabel}
        indexLabel={mapSetting.indexLabel}
        indexRange={[minIndex, maxIndex]}
        excludeRange={[excludeMin, excludeMax]}
        updateMapSettings={(key, value) =>
          dispatch(updateMapSettings(key, value))
        }
        dropdownListTitle="Geography Type"
      />
      <div className="heatMap">
        <USHeatMapChart
          view={view}
          data={currentGeographicsTableData}
          geoLabel={mapSetting.geoLabel}
          indexLabel={mapSetting.indexLabel}
          setIsIframeLoaded={setIsIframeLoaded}
          iframeRef={iframeRef}
        />
      </div>
      <div className="heatMapLegends">
        {currentGeographicsTableData.length ? (
          <GeoMapLegends data={mapLegends} handleAction={handleAction} />
        ) : null}
      </div>

      {isGeoMapTableDataLoading || (view === 'Zip Code' && !isIframeLoaded) ? (
        <div className="geoMapUS__spinner">
          <CustomSpinner />
        </div>
      ) : null}
    </div>
  );
};

GeoUSHeatMap.propTypes = {
  showHeatMap: func,
  handleExportGeoMap: func,
};

export default GeoUSHeatMap;
